import { GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { takeUntil } from "rxjs/operators";
import { Manufacturer, ManufacturerProductTypeConfig } from "../model/entities/entity-model";
import { DomainBaseComponent, wrapBusy } from "../shared/domain-base.component";
import { DomainService } from "../shared/domain.service";
import { EditManufacturerProductTypeConfigDialogComponent } from "./edit-manufacturer-product-type-config-dialog.component";
import { UniAgFns } from "@/shared/uni-ag-fns";

@Component({
  selector: "app-manufacturer-product-type-config",
  templateUrl: "./manufacturer-product-type-config.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ManufacturerProductTypeConfigComponent extends DomainBaseComponent implements OnInit {
  mptConfigGridOptions: GridOptionsExt;

  constructor(protected domainService: DomainService, protected route: ActivatedRoute) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.mptConfigGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onConfigGridReady,
      onRowSelected: this.onRowSelected,
      onFilterChanged: this.onFilterChanged,
      rowModelType: "serverSide",
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.replaceLocation();
        }
      },
    });
    // AgFns.captureGridRouteParams(this.mptConfigGridOptions, this.route, "id");
    this.captureLocation(this.mptConfigGridOptions, "id");

    this.isPageReady = true;
  }

  async canDeactivate() {
    var ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(Manufacturer);

    return true;
  }

  // This method is called AFTER ngAfterViewInit - this is important
  // because the ColDef's here reference TemplateRef's above that will not be resolved until here.
  async onConfigGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps("", this.editMptConfig.bind(this), {
          label: "Edit",
        }),
        ...AgFns.fixWidth(80),
      },
      {
        headerName: "Manufacturer",
        field: "productType.manufacturer.name",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(120),
      },
      {
        headerName: "Description",
        field: "productType.description",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      {
        headerName: "SKU",
        field: "productType.style",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(100),
      },
      {
        headerName: "Configuration Name",
        field: "configName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(140),
      },
      {
        headerName: "Configuration Description",
        field: "configDescription",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      {
        ...AgFns.createCheckboxProps("Stock Only?", "isForStockOrdersOnly"),
        ...AgFns.fixWidth(140),
      },
      { ...UniAgFns.getActiveStatusColDef("activeStatus.name"), ...AgFns.fixWidth(120) },
      UniAgFns.getEntityDeleteColDef(this.onDelete.bind(this), {
        canDisplay: () => true,
      }),
    ];
    const sortModel = [{ colId: "name", sort: "asc" as const }];

    AgFns.initGrid(event, colDefs, sortModel);

    this.updateDatasource();

    await this.applyLocation();
  }

  @wrapBusy
  async onDelete(config: ManufacturerProductTypeConfig) {
    const deleteConfig = await this.dbQueryService.getManufacturerProductTypeConfigById(config.id);
    if (deleteConfig.poDetails.length > 0) {
      await this.dialogService.okDialog(
        "Cannot Delete",
        "Cannot delete Manufacturer Product Type Configuration that has already been used in purchase orders."
      );
      return;
    }
    const ok = await this.dialogService.yesNoDialog("Delete this Manufacturer Product Type Configuration?", "Are you sure?");
    if (!ok) return;

    if (deleteConfig.manufacturerProductTypeConfigAddons.length > 0) {
      deleteConfig.manufacturerProductTypeConfigAddons.forEach((p) => {
        p.entityAspect.setDeleted();
      });
    }
    deleteConfig.entityAspect.setDeleted();
    const sr = await this.dbSaveService.saveChanges();
    if (sr.httpResponse.statusText == "OK") {
      this.toastr.success("Manufacturer Product Type Configuration successfully deleted");
      this.updateDatasource();
    } else {
      this.toastr.error("Manufacturer Product Type Configuration delete failed", "Unable to Delete");
    }
  }

  updateDatasource() {
    const gridApi = this.mptConfigGridOptions.apiX;
    if (gridApi == null) return;
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createManufacturerProductTypeConfigQuery());

    gridApi.setGridOption("serverSideDatasource", ds);
  }

  async onRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) return;
    let manuf = e.data;
    if (!manuf) return;
    this.replaceLocation(manuf.id);
  }

  onFilterChanged() {
    this.replaceLocation();
  }

  async editMptConfig(mptConfig: ManufacturerProductTypeConfig) {
    const r = await DialogBaseComponent.open(EditManufacturerProductTypeConfigDialogComponent, this, {
      manufacturerProductTypeConfig: mptConfig,
    });
    if (r) {
      this.mptConfigGridOptions.apiX.refreshCells();
    }
  }

  async addMptConfig() {
    const r = await DialogBaseComponent.open(EditManufacturerProductTypeConfigDialogComponent, this);
    if (r) {
      this.mptConfigGridOptions.apiX.refreshServerSide();
    }
  }

  // No longer supported.
  // async onDelete(entity: Manufacturer) {
  //   const isInUse = await this.dbQueryService.checkIfInUse(entity, ProductType, 'manufacturerId');
  //   if (isInUse) {
  //     this.dialogWrapperService.showOkMessage('Unable to delete', 'This manufacturer is still being referenced.');
  //     return false;
  //   }
  //   const ynResult = await this.dialogWrapperService.askYesNo('Delete this manufacturer?', 'Are you sure?');
  //   if (ynResult.index != 0) return;
  //   if (entity.manufacturerCryptonym) {
  //     entity.manufacturerCryptonym.entityAspect.setDeleted();
  //   }

  //   entity.entityAspect.setDeleted();
  //   await this.dbSaveService.saveChanges();
  //   this.manufGridOptions.apiX.refreshServerSide();
  //   this.toastr.success('Deleted');
  // }
}
